import React, { useState, useEffect } from 'react';
import { Tabs, Tab, TextField, InputAdornment } from '@material-ui/core';
import SearchIcon from '@material-ui/icons/Search';
import { TableListComp, EmptyCollection, PageLoader, STableLoader } from '../../../../shared_elements';
import { PlannerSideNav, SectionCard } from '../../shared_components';
import { PlanDetailDialog, PlannerCRU, ConfirmActionDialog, ChangePlanStage } from '../../program_planner/components';
import {
  getProgramPlanDetailApi, launchProgramPlanApi, closeProgramPlanApi,
  getStageTemplatesApi, getLessorsListApi, getLesseesListApi, getInspectionTypesApi,
} from '../../program_planner/apiService';
import KpiCards from '../components/KpiCards';
import InactivityBanners from '../components/InactivityBanners';
import DraftRow from '../components/DraftRow';
import OngoingRow from '../components/OngoingRow';
import ClosedRow from '../components/ClosedRow';
import RaiseInvoiceDialog from '../components/RaiseInvoiceDialog';
import { draftHd, ongoingHd, closedHd } from '../constants';
import { buildTrackerListQuery } from '../utils';
import { getTrackerListApi, getTrackerSummaryApi, logPlanActivityApi, raiseInvoiceApi, markInvoicePaidApi } from '../apiService';
import { checkApiStatus } from '../../../../utils_v2';
import { trackActivity } from '../../../../utils/mixpanel';
import { globalGetService, globalPostService } from '../../../../utils_v2/globalApiServices';
import { TEXT, SPACE, pageSx, useSnackbar } from '../../shared_components';
import '../../shared_components/assets/styles/plannerTracker.scss';

const TABS = [
  { key: 'draft', label: 'Draft' },
  { key: 'ongoing', label: 'Ongoing' },
  { key: 'closed', label: 'Closed' },
];

const errorMessage = (response) => response?.data?.message || 'Unable to process the request due to a technical error.';

const ProjectTracker = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [tab, setTab] = useState('ongoing');
  const [plans, setPlans] = useState([]);
  const [pagination, setPagination] = useState({});
  const [tabCounts, setTabCounts] = useState({});
  const [kpis, setKpis] = useState({});
  const [banners, setBanners] = useState([]);
  const [options, setOptions] = useState({ lessors: [], lessees: [], inspectionTypes: [], stageTemplates: [] });
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [resolvingSlug, setResolvingSlug] = useState(null);

  const [viewPlan, setViewPlan] = useState(null);
  const [plannerCRU, setPlannerCRU] = useState({ modal: false, data: null });
  const [stageChangeTarget, setStageChangeTarget] = useState(null);
  const [confirmAction, setConfirmAction] = useState(null);
  const [invoiceTarget, setInvoiceTarget] = useState(null);

  const getOptionsData = () => {
    globalGetService(getLessorsListApi()).then((response) => {
      if (checkApiStatus(response)) {
        const data = response?.data?.data;
        setOptions((prev) => ({ ...prev, lessors: data?.lessors || data || [] }));
      }
    });
    globalGetService(getLesseesListApi()).then((response) => {
      if (checkApiStatus(response)) {
        const data = response?.data?.data;
        setOptions((prev) => ({ ...prev, lessees: data?.lessees || data || [] }));
      }
    });
    globalGetService(getInspectionTypesApi()).then((response) => {
      if (checkApiStatus(response)) {
        setOptions((prev) => ({ ...prev, inspectionTypes: response?.data?.data || [] }));
      }
    });
    globalGetService(getStageTemplatesApi()).then((response) => {
      if (checkApiStatus(response)) {
        setOptions((prev) => ({ ...prev, stageTemplates: response?.data?.data || [] }));
      }
    });
  };

  const fetchSummary = () => {
    globalGetService(getTrackerSummaryApi()).then((response) => {
      if (checkApiStatus(response)) {
        const data = response?.data?.data;
        setKpis(data?.kpis || {});
        setTabCounts(data?.tab_counts || {});
        setBanners(data?.escalation_banners || []);
      }
    });
  };

  const fetchPlans = (overrides = {}) => {
    const params = { tab, page, rowsPerPage, ...overrides };
    setLoading(true);
    globalGetService(getTrackerListApi(), buildTrackerListQuery(params)).then((response) => {
      setLoading(false);
      setInitialLoading(false);
      if (checkApiStatus(response)) {
        const data = response?.data?.data;
        setPlans(data?.list || []);
        setPagination(data?.pagination || {});
      } else {
        enqueueSnackbar(errorMessage(response), { variant: 'error' });
      }
    });
  };

  useEffect(() => {
    getOptionsData();
    fetchSummary();
    fetchPlans();
    trackActivity('Project Management', {
      event_type: 'Page Visited',
      page_title: 'Planner Tracker Project Tracker',
    });
  }, []);

  const refreshAll = () => { fetchPlans(); fetchSummary(); };

  const handleTabChange = (value) => {
    setTab(value);
    setPage(0);
    fetchPlans({ tab: value, page: 0 });
  };

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
    fetchPlans({ page: newPage });
  };

  const handleRowsPerPageChange = (event) => {
    const value = event.target.value;
    setRowsPerPage(value);
    setPage(0);
    fetchPlans({ rowsPerPage: value, page: 0 });
  };

  const handleEditPlan = (target) => {
    setActionLoading(true);
    globalGetService(getProgramPlanDetailApi(target?.slug)).then((response) => {
      setActionLoading(false);
      if (checkApiStatus(response)) {
        setPlannerCRU({ modal: true, data: response?.data?.data });
      } else {
        enqueueSnackbar(errorMessage(response), { variant: 'error' });
      }
    });
  };

  const handleResolve = (planSlug) => {
    setResolvingSlug(planSlug);
    globalPostService(logPlanActivityApi(planSlug), {}).then((response) => {
      setResolvingSlug(null);
      if (checkApiStatus(response)) {
        enqueueSnackbar('Activity logged — inactivity clock reset', { variant: 'success' });
        refreshAll();
        trackActivity('Project Management', {
          event_type: 'Log Plan Activity Success',
          page_title: 'Planner Tracker Project Tracker',
          plan_slug: planSlug || '',
          response_msg: response?.data?.message,
        });
      } else {
        enqueueSnackbar(errorMessage(response), { variant: 'error' });
        trackActivity('Project Management', {
          event_type: 'Log Plan Activity Failed',
          page_title: 'Planner Tracker Project Tracker',
          plan_slug: planSlug || '',
          error_source: 'Backend',
          error_message: errorMessage(response),
        });
      }
    });
  };

  const handleConfirmAction = () => {
    const { type, plan } = confirmAction || {};
    const url = type === 'launch' ? launchProgramPlanApi(plan?.slug)
      : type === 'close' ? closeProgramPlanApi(plan?.slug)
        : markInvoicePaidApi(plan?.slug);
    const actionLabel = type === 'launch' ? 'Launch Plan' : type === 'close' ? 'Close Plan' : 'Mark Invoice Paid';

    setActionLoading(true);
    globalPostService(url, {}).then((response) => {
      setActionLoading(false);
      if (checkApiStatus(response)) {
        enqueueSnackbar(
          type === 'launch' ? 'Project launched' : type === 'close' ? 'Project closed' : 'Invoice marked paid',
          { variant: 'success' },
        );
        setConfirmAction(null);
        refreshAll();
        trackActivity('Project Management', {
          event_type: `${actionLabel} Success`,
          page_title: 'Planner Tracker Project Tracker',
          plan_slug: plan?.slug || '',
          response_msg: response?.data?.message,
        });
      } else {
        enqueueSnackbar(errorMessage(response), { variant: 'error' });
        setConfirmAction(null);
        trackActivity('Project Management', {
          event_type: `${actionLabel} Failed`,
          page_title: 'Planner Tracker Project Tracker',
          plan_slug: plan?.slug || '',
          error_source: 'Backend',
          error_message: errorMessage(response),
        });
      }
    });
  };

  const handleRaiseInvoice = (invoiceRefNo) => {
    setActionLoading(true);
    globalPostService(raiseInvoiceApi(invoiceTarget?.slug), { invoice_ref_no: invoiceRefNo }).then((response) => {
      setActionLoading(false);
      if (checkApiStatus(response)) {
        enqueueSnackbar('Invoice raised', { variant: 'success' });
        setInvoiceTarget(null);
        refreshAll();
        trackActivity('Project Management', {
          event_type: 'Raise Invoice Success',
          page_title: 'Planner Tracker Project Tracker',
          plan_slug: invoiceTarget?.slug || '',
          request_body: { invoice_ref_no: invoiceRefNo },
          response_msg: response?.data?.message,
        });
      } else {
        enqueueSnackbar(errorMessage(response), { variant: 'error' });
        trackActivity('Project Management', {
          event_type: 'Raise Invoice Failed',
          page_title: 'Planner Tracker Project Tracker',
          plan_slug: invoiceTarget?.slug || '',
          request_body: { invoice_ref_no: invoiceRefNo },
          error_source: 'Backend',
          error_message: errorMessage(response),
        });
      }
    });
  };

  return (
    <div style={{ display: 'flex', marginLeft: 56, marginTop: 15 }}>
      <PlannerSideNav activeKey="tracker" />
      <section className="projects-management-apps" style={{ ...pageSx, flex: 1, minWidth: 0, }}>
        <KpiCards kpis={kpis} onSelectTab={handleTabChange} />
        <InactivityBanners banners={banners || []} onResolve={handleResolve} resolvingSlug={resolvingSlug} />

        <SectionCard noPadding style={{ marginBottom: SPACE.md }}>
          <Tabs
            variant="scrollable"
            scrollButtons="auto"
            value={tab}
            onChange={(event, value) => handleTabChange(value)}
            indicatorColor="primary"
            textColor="primary"
          >
            {TABS.map((t) => <Tab key={t.key} value={t.key} label={`${t.label}${tabCounts?.[t.key] !== undefined ? ` (${tabCounts[t.key]})` : ''}`} />)}
          </Tabs>
        </SectionCard>

        {initialLoading ? <STableLoader count={(tab === 'draft' ? draftHd : tab === 'ongoing' ? ongoingHd : closedHd).length} rowsFill={8} /> : (
          <>
            {tab === 'draft' ? (
              <TableListComp
                panelClassName="pt-scroll-panel pt-scroll-panel--tracker-table"
                heads={draftHd}
                data={(plans || []).map((plan) => (
                  <DraftRow
                    key={plan?.id}
                    plan={plan}
                    onLaunch={(p) => setConfirmAction({ type: 'launch', plan: p })}
                    onEdit={handleEditPlan}
                  />
                ))}
                noRecord={!loading && !plans?.length ? <EmptyCollection title="No draft plans found" /> : null}
                pagination={pagination}
                onChangePage={handlePageChange}
                onChangeRowsPerPage={handleRowsPerPageChange}
              />
            ) : null}
            {tab === 'ongoing' ? (
              <TableListComp
                panelClassName="pt-scroll-panel pt-scroll-panel--tracker-table"
                heads={ongoingHd}
                data={(plans || []).map((plan) => (
                  <OngoingRow
                    key={plan?.id}
                    plan={plan}
                    onAdvanceStage={setStageChangeTarget}
                    onCloseProject={(p) => setConfirmAction({ type: 'close', plan: p })}
                    onView={setViewPlan}
                  />
                ))}
                noRecord={!loading && !plans?.length ? <EmptyCollection title="No active projects found" /> : null}
                pagination={pagination}
                onChangePage={handlePageChange}
                onChangeRowsPerPage={handleRowsPerPageChange}
              />
            ) : null}
            {tab === 'closed' ? (
              <TableListComp
                panelClassName="pt-scroll-panel pt-scroll-panel--tracker-table"
                heads={closedHd}
                data={(plans || []).map((plan) => (
                  <ClosedRow
                    key={plan?.id}
                    plan={plan}
                    onView={setViewPlan}
                    onRaiseInvoice={setInvoiceTarget}
                    onMarkPaid={(p) => setConfirmAction({ type: 'mark-paid', plan: p })}
                  />
                ))}
                noRecord={!loading && !plans?.length ? <EmptyCollection title="No closed projects found" /> : null}
                pagination={pagination}
                onChangePage={handlePageChange}
                onChangeRowsPerPage={handleRowsPerPageChange}
              />
            ) : null}
          </>
        )}

        {viewPlan ? (
          <PlanDetailDialog
            plan={viewPlan}
            canLaunch={false}
            canConfigureStages={false}
            onClose={() => setViewPlan(null)}
            onLaunch={() => setConfirmAction({ type: 'launch', plan: viewPlan })}
            onCancelPlan={() => {}}
            onEdit={() => { handleEditPlan(viewPlan); setViewPlan(null); }}
            onConfigureStages={() => {}}
            onChangeInvoiceStatus={() => {}}
            enqueueSnackbar={enqueueSnackbar}
          />
        ) : null}

        {plannerCRU.modal ? (
          <PlannerCRU
            plan={plannerCRU.data}
            lookups={options}
            onClose={() => setPlannerCRU({ modal: false, data: null })}
            onSaved={() => { setPlannerCRU({ modal: false, data: null }); refreshAll(); }}
            enqueueSnackbar={enqueueSnackbar}
          />
        ) : null}

        {stageChangeTarget ? (
          <ChangePlanStage
            plan={stageChangeTarget}
            canOverride
            onClose={() => setStageChangeTarget(null)}
            onSaved={() => { setStageChangeTarget(null); refreshAll(); }}
            enqueueSnackbar={enqueueSnackbar}
          />
        ) : null}

        {confirmAction ? (
          <ConfirmActionDialog
            title={confirmAction?.type === 'launch' ? 'Launch Project' : confirmAction?.type === 'close' ? 'Close Project' : 'Mark Invoice Paid'}
            description={confirmAction?.type === 'launch' ? 'Review data migration before confirming' : confirmAction?.type === 'close' ? 'Both report statuses must be Closed and client acceptance recorded' : null}
            content={`Are you sure you want to ${confirmAction?.type === 'launch' ? 'launch' : confirmAction?.type === 'close' ? 'close' : 'mark as paid'} "${confirmAction?.plan?.name}"?`}
            confirmText={confirmAction?.type === 'launch' ? 'Confirm Launch' : confirmAction?.type === 'close' ? 'Confirm Close' : 'Confirm'}
            confirmColor="primary"
            loading={actionLoading}
            onClose={() => setConfirmAction(null)}
            onConfirm={handleConfirmAction}
          />
        ) : null}

        {invoiceTarget ? (
          <RaiseInvoiceDialog
            plan={invoiceTarget}
            loading={actionLoading}
            onClose={() => setInvoiceTarget(null)}
            onSubmit={handleRaiseInvoice}
          />
        ) : null}

        {!initialLoading && (loading || actionLoading) ? <PageLoader /> : null}
      </section>
    </div>
  );
};

export default ProjectTracker;
